import { createTheme, alpha } from '@mui/material/styles'

// ──────────────────────────────────────────────────────────────
// Brand palette (mode-agnostic)
// ──────────────────────────────────────────────────────────────
const brand = {
  primary: '#3B82F6',
  primaryDark: '#344682',
  secondary: '#5e7dff',
  success: '#1fd8a8',
  info: '#5ea6ff',
  warning: '#ffa65c',
  danger: '#ff3d57',
  dangerDark: '#dc3450',
  successDark: '#17b594',
  warningDark: '#ff971f',
  infoDark: '#4c8dff',
}

// ──────────────────────────────────────────────────────────────
// Dark palette
// ──────────────────────────────────────────────────────────────
const darkPalette = {
  mode: 'dark',
  primary: brand.primary,
  primaryDark: brand.primaryDark,
  secondary: brand.secondary,
  success: brand.success,
  info: brand.info,
  warning: brand.warning,
  danger: brand.danger,

  // Surfaces
  background: '#131C30',
  paper: '#18233C',
  cardBorder: 'rgba(255,255,255,0.12)',

  // Text
  textPrimary: '#f8fbff',
  textSecondary: '#6c7b99',
  textMuted: '#A4B2CD',
  headingColor: '#ffffff',

  // Tabs
  tabBg: '#131C30',

  // Tables
  tableBg: '#18233c',
  tableText: '#A4B2CD',
  tableHeaderText: '#A4B2CD',
  tableRowHover: 'rgba(24,35,60,0.3)',
  tableBorder: 'rgba(255,255,255,0.05)',

  // Inputs
  inputBg: '#1F2C49',
  inputText: '#EEF3FB',
  inputPlaceholder: '#7a8aa8',
  inputBorder: 'rgba(255,255,255,0.14)',

  // Header / app bar
  headerBg: '#0D1427',
  headerIconColor: '#ffffff',

  // Misc UI
  divider: 'rgba(255,255,255,0.08)',
  modalBg: '#131c30',
  modalHeader: '#EEF3FB',
  menuHover: 'rgba(59,130,246,0.05)',
  menuSelected: 'rgba(46,133,235,0.10)',
  checkboxColor: '#64748B',
  checkboxHoverBg: 'rgba(255, 255, 255, 0.06)',
  checkboxChecked: '#3B82F6',
  checkboxDisabled: 'rgba(100, 116, 139, 0.4)',
  chipBg: '#18233c',
  chipText: '#A4B2CD',
  chipBorder: 'rgba(255,255,255,0.08)',
  chipFilledBg: 'rgba(255,255,255,0.06)',
  btnErrorDisabledBg: 'rgba(255, 61, 87, 0.18)',
  btnErrorDisabledText: 'rgba(255, 255, 255, 0.55)',
  outlinedBtnBorder: 'rgba(255,255,255,0.14)',
  outlinedBtnText: '#ffffff',
  outlinedNeutralBorder: 'rgba(255,255,255,0.14)',
  outlinedNeutralText: '#EEF3FB',
  dialogShadow: 'none',
  boxCloudBurst: '#1F2C49',
  trackBg: 'rgba(255,255,255,0.18)',
  selectionBg: 'rgba(59,130,246,0.45)',
  selectionColor: '#ffffff',
  scrollbarTrack: '#131C30',
  scrollbarThumb: 'rgba(128,128,128,0.35)',
  scrollbarThumbHover: 'rgba(128,128,128,0.55)',
}

// ──────────────────────────────────────────────────────────────
// Light palette
// ──────────────────────────────────────────────────────────────
const lightPalette = {
  mode: 'light',
  primary: brand.primary,
  primaryDark: brand.primaryDark,
  secondary: brand.secondary,
  success: brand.success,
  info: brand.info,
  warning: brand.warning,
  danger: brand.danger,

  // Surfaces
  background: '#f6f8fc',
  paper: '#ffffff',
  cardBorder: 'rgba(59,130,246,0.12)',

  // Text
  textPrimary: '#1f2b44',
  textSecondary: '#475569',
  textMuted: '#64748B',
  headingColor: '#1f2b44',

  // Tabs
  tabBg: '#3B82F6',

  // Tables
  tableBg: '#ffffff',
  tableText: '#6C7B99',
  tableHeaderText: '#475569',
  tableRowHover: '#f1f5f9',
  tableBorder: 'rgba(15, 23, 42, 0.08)',

  // Inputs
  inputBg: '#FFFFFF',
  inputText: '#6C7B99',
  inputPlaceholder: '#94a3b8',
  inputBorder: 'rgba(0,0,0,0.2)',

  // Header / app bar
  headerBg: '#ffffff',
  headerIconColor: '#08122f',

  // Misc UI
  divider: 'rgba(59,130,246,0.15)',
  modalBg: '#ffffff',
  modalHeader: '#1F2C49',
  menuHover: '#f1f5f9',
  menuSelected: 'rgba(59,130,246,0.12)',
  checkboxColor: '#94a3b8',
  checkboxHoverBg: 'rgba(15, 23, 42, 0.06)',
  checkboxChecked: '#3B82F6',
  checkboxDisabled: 'rgba(148, 163, 184, 0.5)',
  chipBg: '#ffffff',
  chipText: '#64748B',
  chipBorder: 'rgba(59,130,246,0.18)',
  chipFilledBg: 'rgba(59,130,246,0.15)',
  btnErrorDisabledBg: '#fecdd3',
  btnErrorDisabledText: '#9f1239',
  outlinedBtnBorder: '#3B82F6',
  outlinedBtnText: '#3B82F6',
  outlinedNeutralBorder: 'rgba(15,23,42,0.14)',
  outlinedNeutralText: '#1f2b44',
  dialogShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  boxCloudBurst: 'rgba(59,130, 246, 0.15)',
  trackBg: '#e9ebec',
  selectionBg: 'rgba(59,130,246,0.55)',
  selectionColor: '#ffffff',
  scrollbarTrack: '#f6f8fc',
  scrollbarThumb: 'rgba(100,116,139,0.4)',
  scrollbarThumbHover: 'rgba(100,116,139,0.6)',
}

// ──────────────────────────────────────────────────────────────
// Sort icons (SVG data URLs)
// ──────────────────────────────────────────────────────────────
const SORT_ASC =
  "url(\"data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.88628e-05 8.7124L5.03755 -9.799e-05L10.0625 8.7124H4.88628e-05Z' fill='%237EB0FF'/%3E%3C/svg%3E\")"
const SORT_DESC =
  "url(\"data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.88628e-05 -0.000106052L5.03755 8.71239L10.0625 -0.000106052H4.88628e-05Z' fill='%237EB0FF'/%3E%3C/svg%3E\")"

// ──────────────────────────────────────────────────────────────
// Shared typography
// ──────────────────────────────────────────────────────────────
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: 14,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
  h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.25 },
  h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3 },
  h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.35 },
  h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
  body1: { fontSize: '0.875rem', lineHeight: 1.65 },
  body2: { fontSize: '0.75rem', lineHeight: 1.6 },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 500 },
  caption: { fontSize: '0.70rem', fontWeight: 500 },
}

// ──────────────────────────────────────────────────────────────
// Shared shadows
// ──────────────────────────────────────────────────────────────
const shadows = [
  'none',
  '0 0 3px rgba(64, 81, 137, 0.05), 0 0 3px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.05), 0 0 6px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.05), 0 0 10px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.05), 0 0 15px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.08), 0 0 20px rgba(64, 81, 137, 0.08)',
  '0 0 3px rgba(64, 81, 137, 0.08), 0 0 25px rgba(64, 81, 137, 0.08)',
  '0 0 3px rgba(64, 81, 137, 0.1), 0 0 30px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.1), 0 0 35px rgba(64, 81, 137, 0.1)',
  '0 0 3px rgba(64, 81, 137, 0.12), 0 0 40px rgba(64, 81, 137, 0.12)',
  '0 0 3px rgba(64, 81, 137, 0.12), 0 0 45px rgba(64, 81, 137, 0.12)',
  '0 0 3px rgba(64, 81, 137, 0.14), 0 0 50px rgba(64, 81, 137, 0.14)',
  '0 0 3px rgba(64, 81, 137, 0.14), 0 0 55px rgba(64, 81, 137, 0.14)',
  '0 0 3px rgba(64, 81, 137, 0.16), 0 0 60px rgba(64, 81, 137, 0.16)',
  '0 0 3px rgba(64, 81, 137, 0.16), 0 0 65px rgba(64, 81, 137, 0.16)',
  '0 0 3px rgba(64, 81, 137, 0.18), 0 0 70px rgba(64, 81, 137, 0.18)',
  '0 0 3px rgba(64, 81, 137, 0.18), 0 0 75px rgba(64, 81, 137, 0.18)',
  '0 0 3px rgba(64, 81, 137, 0.2), 0 0 80px rgba(64, 81, 137, 0.2)',
  '0 0 3px rgba(64, 81, 137, 0.2), 0 0 85px rgba(64, 81, 137, 0.2)',
  '0 0 3px rgba(64, 81, 137, 0.22), 0 0 90px rgba(64, 81, 137, 0.22)',
  '0 0 3px rgba(64, 81, 137, 0.22), 0 0 95px rgba(64, 81, 137, 0.22)',
  '0 0 3px rgba(64, 81, 137, 0.24), 0 0 100px rgba(64, 81, 137, 0.24)',
  '0 0 6px rgba(64, 81, 137, 0.26), 0 0 110px rgba(64, 81, 137, 0.26)',
  '0 0 6px rgba(64, 81, 137, 0.28), 0 0 120px rgba(64, 81, 137, 0.28)',
  '0 0 6px rgba(64, 81, 137, 0.3), 0 0 130px rgba(64, 81, 137, 0.3)',
]

// ──────────────────────────────────────────────────────────────
// Build a complete MUI theme for a given palette
// ──────────────────────────────────────────────────────────────
function buildTheme(p) {
  return createTheme({
    palette: {
      mode: p.mode,
      primary: {
        main: p.primary,
        light: alpha(p.primary, 0.85),
        dark: brand.primaryDark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: p.secondary,
        light: alpha(p.secondary, 0.85),
        dark: '#4f6eff',
        contrastText: '#ffffff',
      },
      success: {
        main: p.success,
        light: alpha(p.success, 0.85),
        dark: brand.successDark,
      },
      info: {
        main: p.info,
        light: alpha(p.info, 0.85),
        dark: brand.infoDark,
      },
      warning: {
        main: p.warning,
        light: alpha(p.warning, 0.85),
        dark: brand.warningDark,
      },
      error: {
        main: p.danger,
        light: alpha(p.danger, 0.85),
        dark: brand.dangerDark,
      },
      dark: {
        main: '#0D1427',
        light: '#0f1b40',
        dark: '#061020',
        contrastText: '#ffffff',
      },
      background: {
        default: p.background,
        paper: p.paper,
      },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
        disabled: p.textMuted,
      },
      divider: p.divider,
      grey: {
        50: '#f4f7ff',
        100: '#dfe7ff',
        200: '#b8c8ee',
        300: '#8998c4',
        400: '#6f80b6',
        500: '#566a9b',
        600: '#45567f',
        700: '#33415f',
        800: '#1f2b44',
        900: '#12192f',
      },
    },
    typography: {
      ...typography,
      color: p.textPrimary,
    },
    shape: { borderRadius: 6 },
    shadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            '--primary': p.primary,
            '--success': p.success,
            '--danger': p.danger,
            '--warning': p.warning,
            '--info': p.info,
            '--heading-color': p.headingColor,
            '--body-bg': p.background,
            '--header-bg': p.headerBg,
            '--header-icon-color': p.headerIconColor,
            '--text-primary': p.textPrimary,
            '--text-secondary': p.textSecondary,
            '--text-muted': p.textMuted,
            '--card-bg': p.paper,
            '--card-text': p.textPrimary,
            '--card-border': p.cardBorder,
            '--tab-bg-color': p.tabBg,
            '--table-bg': p.tableBg,
            '--table-text': p.tableText,
            '--table-header-text': p.tableHeaderText,
            '--table-row-hover': p.tableRowHover,
            '--table-border': p.tableBorder,
            '--filter-chip-bg': p.chipBg,
            '--filter-chip-text': p.chipText,
            '--filter-chip-border': p.chipBorder,
            '--chip-filled-bg': p.chipFilledBg,
            '--chip-filled-border': p.primary,
            '--input-bg': p.inputBg,
            '--input-text': p.inputText,
            '--input-placeholder': p.inputPlaceholder,
            '--input-border': p.inputBorder,
            '--modal-bg-color': p.modalBg,
            '--modal-header-color': p.modalHeader,
            '--divider-color': p.divider,
            '--menu-hover-bg': p.menuHover,
            '--menu-selected-bg': p.menuSelected,
            '--checkbox-color': p.checkboxColor,
            '--checkbox-hover-bg': p.checkboxHoverBg,
            '--checkbox-checked-color': p.checkboxChecked,
            '--checkbox-disabled-color': p.checkboxDisabled,
            '--btn-error-disabled-bg': p.btnErrorDisabledBg,
            '--btn-error-disabled-text': p.btnErrorDisabledText,
            '--outlined-btn-border': p.outlinedBtnBorder,
            '--outlined-btn-text': p.outlinedBtnText,
            '--outlined-neutral-border': p.outlinedNeutralBorder,
            '--outlined-neutral-text': p.outlinedNeutralText,
            '--dialog-shadow': p.dialogShadow,
            '--box-cloud-burst': p.boxCloudBurst,
            '--track-bg': p.trackBg,
            '--selection-bg': p.selectionBg,
            '--selection-color': p.selectionColor,
            '--sort-asc-icon': SORT_ASC,
            '--sort-desc-icon': SORT_DESC,
          },
          '::selection': {
            backgroundColor: 'var(--selection-bg)',
            color: 'var(--selection-color)',
          },
          '::-moz-selection': {
            backgroundColor: 'var(--selection-bg)',
            color: 'var(--selection-color)',
          },
          body: {
            backgroundColor: p.background,
            color: p.textPrimary,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-track': { background: p.scrollbarTrack },
            '&::-webkit-scrollbar-thumb': {
              background: p.scrollbarThumb,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': { background: p.scrollbarThumbHover },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            fontSize: '0.875rem',
            textTransform: 'none',
            boxShadow: 'none',
            transition:
              'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { boxShadow: 'none' },
          },
          contained: {
            '&:hover': { boxShadow: '0 2px 6px rgba(64, 81, 137, 0.25)' },
          },
          containedPrimary: {
            background: brand.primary,
            color: '#ffffff',
            '&:hover': { background: brand.primaryDark },
            '&.Mui-disabled': {
              background: 'rgba(59,130,246,0.4)',
              color: '#ffffff',
            },
          },
          outlinedPrimary: {
            borderColor: brand.primary,
            color: brand.primary,
            '&:hover': {
              borderColor: brand.primaryDark,
              backgroundColor: alpha(brand.primary, 0.06),
            },
          },
          textPrimary: {
            color: brand.primary,
            '&:hover': { backgroundColor: alpha(brand.primary, 0.06) },
          },
          containedSecondary: {
            background: brand.secondary,
            color: '#ffffff',
            '&:hover': { background: '#4f6eff' },
          },
          outlinedSecondary: {
            borderColor: brand.secondary,
            color: brand.secondary,
            '&:hover': {
              borderColor: '#4f6eff',
              backgroundColor: alpha(brand.secondary, 0.06),
            },
          },
          textSecondary: {
            color: brand.secondary,
            '&:hover': { backgroundColor: alpha(brand.secondary, 0.06) },
          },
          containedSuccess: {
            background: brand.success,
            color: '#061020',
            '&:hover': { background: brand.successDark },
          },
          outlinedSuccess: {
            borderColor: brand.success,
            color: brand.success,
            '&:hover': {
              borderColor: brand.successDark,
              backgroundColor: alpha(brand.success, 0.08),
            },
          },
          textSuccess: {
            color: brand.success,
            '&:hover': { backgroundColor: alpha(brand.success, 0.08) },
          },
          containedError: {
            background: brand.danger,
            color: '#ffffff',
            '&:hover': { background: brand.dangerDark },
            '&.Mui-disabled': {
              background: 'var(--btn-error-disabled-bg)',
              color: 'var(--btn-error-disabled-text)',
            },
          },
          outlinedError: {
            borderColor: brand.danger,
            color: brand.danger,
            '&:hover': {
              borderColor: brand.dangerDark,
              backgroundColor: alpha(brand.danger, 0.06),
            },
          },
          textError: {
            color: brand.danger,
            '&:hover': { backgroundColor: alpha(brand.danger, 0.06) },
          },
          containedWarning: {
            background: brand.warning,
            color: '#061020',
            '&:hover': { background: brand.warningDark },
          },
          outlinedWarning: {
            borderColor: brand.warning,
            color: brand.warning,
            '&:hover': {
              borderColor: brand.warningDark,
              backgroundColor: alpha(brand.warning, 0.08),
            },
          },
          textWarning: {
            color: brand.warning,
            '&:hover': { backgroundColor: alpha(brand.warning, 0.08) },
          },
          containedInfo: {
            background: brand.info,
            color: '#061020',
            '&:hover': { background: brand.infoDark },
          },
          outlinedInfo: {
            borderColor: brand.info,
            color: brand.info,
            '&:hover': {
              borderColor: brand.infoDark,
              backgroundColor: alpha(brand.info, 0.08),
            },
          },
          textInfo: {
            color: brand.info,
            '&:hover': { backgroundColor: alpha(brand.info, 0.08) },
          },
          outlined: {
            borderColor: 'var(--outlined-btn-border)',
            color: 'var(--outlined-btn-text)',
            '&:hover': {
              borderColor: 'var(--outlined-btn-border)',
              backgroundColor: alpha(brand.primary, 0.04),
            },
          },
          outlinedNeutral: {
            border: '1px solid var(--outlined-neutral-border)',
            color: 'var(--outlined-neutral-text)',
            '&:hover': {
              borderColor: 'var(--outlined-neutral-border)',
              backgroundColor: alpha('#ffffff', 0.04),
            },
          },
          text: {
            color: 'var(--outlined-btn-text)',
            '&:hover': { backgroundColor: alpha(brand.primary, 0.04) },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 0 3px rgba(64, 81, 137, 0.05), 0 0 3px rgba(64, 81, 137, 0.1)',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--card-text)',
            border: '1px solid var(--card-border)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--card-text)',
            border: '1px solid var(--card-border)',
          },
          rounded: { borderRadius: 16 },
          elevation1: { boxShadow: '0 18px 45px rgba(0, 0, 0, 0.18)' },
          elevation2: { boxShadow: '0 22px 60px rgba(0, 0, 0, 0.18)' },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            '&:last-child': { paddingBottom: '24px' },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            borderBottom: `1px solid ${p.divider}`,
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            '& .MuiStepConnector-line': {
              borderColor: 'var(--divider-color)',
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            '& .MuiStepConnector-line': {
              borderColor: 'var(--divider-color)',
            },
            '&.Mui-active': {
              '& .MuiStepConnector-line': {
                borderColor: '#2FBF71',
              },
            },
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          root: {
            '& .MuiStepConnector-line': {
              borderColor: 'var(--divider-color)',
            },
            '&.Mui-completed': {
              '& .MuiStepConnector-line': {
                borderColor: '#2FBF71',
              },
              '& .MuiStepIcon-root': {
                backgroundColor: 'rgba(47, 191, 113, 0.15)',
                border: '2px solid #2FBF71',
                borderRadius: '50%',
                fill: 'rgba(47, 191, 113, 0.15)',
                '& path': { fill: '#2FBF71' },
                '& circle': { display: 'none' },
                '& text': { fill: '#2FBF71', fontWeight: 700 },
              },
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: 'var(--input-border)',
            width: 26,
            height: 26,
            '&.Mui-active': { color: brand.primary },
            '&.Mui-completed': {
              color: '#2FBF71',
              backgroundColor: 'rgba(47, 191, 113, 0.15)',
              border: '2px solid #2FBF71',
              borderRadius: '50%',
              '& circle': { display: 'none' },
              '& text': { fill: '#fff', fontWeight: 700, fontSize: '12px' },
            },
          },
          text: {
            fill: '#fff',
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            '&.Mui-completed': {
              color: '#2FBF71',
            },
            '&.Mui-active': { color: brand.primary, fontSize: '12px' },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              backgroundColor: 'var(--input-bg)',
              color: 'var(--input-text)',
              fontSize: '13px',
              '& fieldset': { borderColor: 'var(--input-border)' },
              '&:hover fieldset': { borderColor: brand.primary },
              '&.Mui-focused fieldset': { borderColor: brand.primary },
              // Chrome/Edge ignore `background-color` on `:-webkit-autofill`
              // (browser-saved credentials), so the field flashes the agent
              // blue when autofilled. Paint a large inset box-shadow matching
              // `--input-bg` to keep the dark-theme styling consistent.
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px var(--input-bg) inset',
                WebkitTextFillColor: 'var(--input-text)',
                caretColor: 'var(--input-text)',
              },
              '& input:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 100px var(--input-bg) inset',
              },
              '& input:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 100px var(--input-bg) inset',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'var(--input-placeholder)',
              fontSize: '13px',
              '&.MuiInputLabel-shrink': { fontSize: '13.8px' },
              '&.Mui-focused': { color: brand.primary },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: '13px',
          },
          input: {
            padding: '10px 14px',
            color: 'var(--input-text)',
            fontSize: '13px',
            '&::placeholder': { color: 'var(--input-placeholder)', opacity: 1 },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: 'var(--input-placeholder)',
            fontSize: '13px',
            '&.Mui-focused': { color: `${brand.primary} !important` },
            '& .MuiInputLabel-asterisk': { color: 'var(--danger, #ff3d57)' },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: { color: 'var(--input-text)', fontSize: '13px' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: '13px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--input-border)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: brand.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: brand.primary },
          },
          input: {
            color: 'var(--input-text)',
            fontSize: '13px',
            '&::placeholder': { color: 'var(--input-placeholder)', opacity: 1 },
          },
          inputMedia: {
            color: 'var(--input-text)',
            fontSize: '13px',
            '&::placeholder': { color: 'var(--input-placeholder)', opacity: 1 },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: 'var(--input-placeholder)' },
          select: {
            color: 'var(--input-text)',
            fontSize: '13px',
            backgroundColor: 'var(--input-bg)',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--input-bg)',
              color: 'var(--input-text)',
              fontSize: '13px',
            },
            '& .MuiInputBase-input': {
              color: 'var(--input-text)',
              fontSize: '13px',
              '&::placeholder': { color: 'var(--input-placeholder)', opacity: 1 },
            },
            '& .MuiAutocomplete-popupIndicator': { color: 'var(--input-placeholder)' },
            '& .MuiAutocomplete-clearIndicator': { color: 'var(--input-placeholder)' },
          },
          paper: {
            backgroundColor: 'var(--input-bg)',
            color: 'var(--input-text)',
            border: '1px solid var(--input-border)',
          },
          listbox: {
            backgroundColor: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: '13px',
            '& .MuiAutocomplete-option': {
              color: 'var(--input-text)',
              fontSize: '13px',
              '&:hover': { backgroundColor: 'var(--menu-hover-bg)' },
              '&[aria-selected="true"]': { backgroundColor: 'var(--menu-selected-bg)' },
              '&[aria-selected="true"]:hover': { backgroundColor: 'var(--menu-selected-bg)' },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--table-bg)',
            '& .MuiTableCell-root': {
              fontWeight: 500,
              fontSize: '14px',
              color: 'var(--table-header-text)',
              borderBottom: '1px solid var(--table-border)',
              padding: '12px 14px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: '14px',
            color: 'var(--table-text)',
            borderBottom: '1px solid var(--table-border)',
            padding: '12px 14px',
            backgroundColor: 'var(--table-bg)',
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: 'var(--table-text) !important',
            '& .MuiTableSortLabel-icon': { opacity: 1, width: 16, height: 16 },
            '&.Mui-active': {
              color: 'var(--table-text) !important',
              '& .MuiTableSortLabel-icon': { opacity: 1 },
            },
          },
          icon: { color: '#7EB0FF' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover .MuiTableCell-root': {
              backgroundColor: 'var(--table-row-hover) !important',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 4,
            color: 'var(--checkbox-color)',
            borderRadius: 3,
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': { backgroundColor: 'var(--checkbox-hover-bg)' },
            '&.Mui-checked': { color: 'var(--checkbox-checked-color)' },
            '&.Mui-indeterminate': { color: 'var(--checkbox-checked-color)' },
            '&.Mui-disabled': { color: 'var(--checkbox-disabled-color)' },
            '& .MuiSvgIcon-root': { fontSize: '1.05rem' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            fontSize: '0.75rem',
            backgroundColor: 'var(--filter-chip-bg)',
            color: 'var(--filter-chip-text)',
            borderColor: 'var(--filter-chip-border)',
          },
          outlined: {
            borderColor: 'var(--filter-chip-border)',
            backgroundColor: 'var(--filter-chip-bg)',
            color: 'var(--filter-chip-text)',
          },
          filled: {
            backgroundColor: 'var(--chip-filled-bg)',
            color: 'var(--filter-chip-text)',
            // border: '1px solid var(--chip-filled-border)',
            '&.MuiChip-colorSuccess': {
              backgroundColor: alpha(brand.success, 0.15),
              color: brand.success,
            },
            '&.MuiChip-colorError': {
              backgroundColor: alpha(brand.danger, 0.15),
              color: brand.danger,
            },
            '&.MuiChip-colorWarning': {
              backgroundColor: alpha(brand.warning, 0.15),
              color: '#b47c00',
            },
            '&.MuiChip-colorInfo': {
              backgroundColor: alpha(brand.info, 0.15),
              color: brand.info,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#212529',
            fontSize: '0.75rem',
            padding: '6px 12px',
            borderRadius: 4,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--card-border)',
            backgroundColor: 'var(--modal-bg-color)',
            color: 'var(--input-text)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: '10px 16px',
            fontSize: '0.875rem',
            color: 'var(--input-text)',
            '&:hover': { backgroundColor: 'var(--sidebar-hover-bg)' },
            '&.Mui-selected': { backgroundColor: 'var(--sidebar-active-bg)' },
            '&.Mui-selected:hover': { backgroundColor: 'var(--sidebar-active-bg)' },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 8px',
            '&:hover': { backgroundColor: alpha(brand.primary, 0.08) },
            '&.Mui-selected': {
              backgroundColor: alpha(brand.primary, 0.12),
              '&:hover': { backgroundColor: alpha(brand.primary, 0.16) },
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: { borderRadius: 0 },
          paper: {
            borderRight: 'none',
            borderRadius: 0,
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            borderBottom: `1px solid ${p.divider}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            backgroundColor: 'var(--modal-bg-color)',
            backgroundImage: 'none',
            border: '1px solid var(--card-border)',
            maxHeight: '92vh',
            boxShadow: 'var(--dialog-shadow)',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { px: 3, py: 2.5 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: '1.125rem' },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 44,
            '&.Mui-selected': { color: brand.primary },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, backgroundColor: '#e9ebec' },
          bar: { borderRadius: 4 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
          standardSuccess: { backgroundColor: alpha(brand.success, 0.1), color: brand.success },
          standardError: { backgroundColor: alpha(brand.danger, 0.1), color: brand.danger },
          standardWarning: { backgroundColor: alpha(brand.warning, 0.1), color: '#b47c00' },
          standardInfo: { backgroundColor: alpha(brand.info, 0.1), color: brand.info },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { backgroundColor: brand.primary, color: '#ffffff', fontWeight: 500 },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 600, fontSize: '0.625rem' },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              borderRadius: 6,
              '&.Mui-selected': {
                backgroundColor: brand.primary,
                color: '#ffffff',
                '&:hover': { backgroundColor: brand.primaryDark },
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 46, height: 26, padding: 0 },
          switchBase: {
            padding: 0,
            margin: '3px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': { backgroundColor: 'var(--primary)', opacity: 1, border: 0 },
            },
          },
          thumb: {
            boxSizing: 'border-box',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          },
          track: {
            borderRadius: 13,
            backgroundColor: 'var(--track-bg, rgba(255,255,255,0.18))',
            opacity: 1,
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: 'var(--divider-color)' } },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            '& .MuiBreadcrumbs-li': { fontSize: '0.8125rem', color: 'var(--input-text)' },
            '& a': {
              color: 'var(--input-text)',
              textDecoration: 'none',
              '&:hover': { color: 'var(--primary)' },
            },
            '& .MuiTypography-root.MuiTypography-body2:last-of-type': {
              color: 'var(--heading-color)',
              fontWeight: 500,
            },
          },
          separator: {
            marginLeft: 8,
            marginRight: 8,
            color: 'var(--input-text)',
            opacity: 0.6,
            fontSize: '0.875rem',
            lineHeight: 1,
          },
        },
      },
    },
  })
}

// ──────────────────────────────────────────────────────────────
// Exported themes + helper
// ──────────────────────────────────────────────────────────────
export const darkTheme = buildTheme(darkPalette)
export const lightTheme = buildTheme(lightPalette)

export function getTheme(mode) {
  return mode === 'light' ? lightTheme : darkTheme
}

// Backwards-compatible default export (dark mode)
const theme = darkTheme
export default theme

export { brand, darkPalette, lightPalette }
